'use client';

import { ComposerPrimitive, useAuiState } from '@assistant-ui/react';
import { Button } from '@iq/ui';
import { ArrowUpIcon, Loader2Icon } from 'lucide-react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { TooltipIconButton } from '../ui/tooltip-icon-button';

interface ComposerAttachmentSlice {
  status?: { type?: string } | undefined;
}

/**
 * Composer Send button that stays disabled while ANY attachment is still
 * uploading (status `running`). ChatGPT behaviour: even with a typed prompt,
 * the user cannot send until every attachment finishes — so the wire message
 * always carries a fully-resolved `upload_id`, never a half-uploaded ref
 * (which the server would silently drop, leaving the turn empty → 422).
 *
 * Why swap the rendered element instead of passing `disabled` to
 * `ComposerPrimitive.Send`: with `asChild`, Radix `Slot` lets the primitive's
 * own `disabled` (derived from `canSend`, which ignores attachment status)
 * win over a child `disabled` prop — so a child-level disable is unreliable.
 * Rendering a plain disabled `Button` with a spinner while uploading is the
 * robust seam, and we fall back to the real `ComposerPrimitive.Send` the
 * moment every attachment reaches `requires-action` / `complete`.
 */
export const ComposerSendButton: FC = () => {
  const { t } = useTranslation();
  // Block while any attachment is still uploading (`running`).
  const uploading = useAuiState((s) => {
    const attachments = (s as { composer: { attachments: readonly ComposerAttachmentSlice[] } })
      .composer.attachments;
    return attachments.some((a) => a.status?.type === 'running');
  });
  // Also block when an attachment FAILED to upload (`incomplete`). Sending it
  // would ship a half-uploaded ref the server drops → a confusing empty-turn
  // 422. The user removes the failed chip (× on the attachment) to proceed.
  const errored = useAuiState((s) => {
    const attachments = (s as { composer: { attachments: readonly ComposerAttachmentSlice[] } })
      .composer.attachments;
    return attachments.some((a) => a.status?.type === 'incomplete');
  });

  if (uploading) {
    return (
      <Button
        type="button"
        variant="default"
        size="icon"
        disabled
        className="aui-composer-send size-8 rounded-full"
        aria-label={t('chat.composer.uploading')}
      >
        <Loader2Icon className="aui-composer-send-icon size-4 animate-spin" />
      </Button>
    );
  }

  if (errored) {
    return (
      <Button
        type="button"
        variant="default"
        size="icon"
        disabled
        className="aui-composer-send size-8 rounded-full"
        aria-label={t('chat.composer.sendMessage')}
      >
        <ArrowUpIcon className="aui-composer-send-icon size-4" />
      </Button>
    );
  }

  return (
    <ComposerPrimitive.Send asChild>
      <TooltipIconButton
        tooltip={t('chat.composer.sendMessage')}
        side="bottom"
        type="button"
        variant="default"
        size="icon"
        className="aui-composer-send size-8 rounded-full"
        aria-label={t('chat.composer.sendMessage')}
      >
        <ArrowUpIcon className="aui-composer-send-icon size-4" />
      </TooltipIconButton>
    </ComposerPrimitive.Send>
  );
};
